import React, { useRef } from 'react';
import { bool, func, shape, string } from 'prop-types';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { FieldArray } from 'react-final-form-arrays';
import { FormattedMessage } from '../../util/reactIntl';
import { findOptionsForSelectFilter } from '../../util/search';
import { propTypes, PICKUP } from '../../util/types';
import config from '../../config';
import { OnChange } from 'react-final-form-listeners';
import {
  required, moneySubUnitAmountAtLeast, composeValidators, nonEmptyArray,
} from '../../util/validators';
import {
  EditListingActionButton,
  FieldCheckboxGroup,
  Form,
  FieldCurrencyInput,
  LocationAutocompleteInputField,
  InlineTextButton,
  ValidationError,
} from '../../components';
import { types as sdkTypes } from '../../util/sdkLoader';
import css from './EditListingFeaturesForm.css';
import { formatMoney } from '../../util/currency';

const identity = v => v;
const { Money } = sdkTypes;

const EditListingFeaturesFormComponent = props => {
  const submittedValues = useRef(null);

  return (
    <FinalForm
      {...props}
      initialValues={{
        ...(submittedValues.current ? submittedValues.current : props.initialValues),
      }}
      mutators={{ ...arrayMutators }}
      render={formRenderProps => {
        const {
          disabled,
          ready,
          rootClassName,
          className,
          handleSubmit,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          filterConfig,
          form,
          intl,
          invalid,
          onBackWithoutSubmit,
          values,
        } = formRenderProps;

        const deliveryTypeLabel = intl.formatMessage({
          id: 'EditListingFeaturesForm.deliveryTypeLabel',
        });
        const deliveryTypeRequireMessage = intl.formatMessage({
          id: 'EditListingFeaturesForm.deliveryTypeRequireMessage',
        });

        const deliveryFeeLabel = intl.formatMessage({
          id: 'EditListingFeaturesForm.deliveryFeeLabel',
        });
        const deliveryFeeDescriptionMessage = intl.formatMessage({
          id: 'EditListingFeaturesForm.deliveryFeeDescriptionMessage',
        });

        const addressLabelMessage = index =>
          intl.formatMessage({ id: 'EditListingFeaturesForm.address' }, { index: index });
        const addressPlaceholderMessage = intl.formatMessage({
          id: 'EditListingFeaturesForm.addressPlaceholder',
        });

        const isDeliveryValid = formValue => {
          const { deliveryType, locations } = formValue;
          const isContainDeliveryType = deliveryType.some(type => type === 'delivery');
          const isLocationsValid = locations.length && locations.every(
            location => ( location !== null && location.selectedPlace !== null )
          );

          /** Function return true if:
           * 1. is NOT including delivery type
           * 2. is including delivery type AND list of location is valid
           **/
          return !isContainDeliveryType || isLocationsValid;
        };

        const classes = classNames(rootClassName || css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = disabled || submitInProgress || !isDeliveryValid(values);

        const { updateListingError, showListingsError } = fetchErrors || {};
        const errorMessage = updateListingError ? (
          <p className={css.error}>
            <FormattedMessage id="EditListingFeaturesForm.updateFailed" />
          </p>
        ) : null;

        const errorMessageShowListing = showListingsError ? (
          <p className={css.error}>
            <FormattedMessage id="EditListingFeaturesForm.showListingFailed" />
          </p>
        ) : null;

        const options = findOptionsForSelectFilter('deliveryTypes', filterConfig);

        const priceRequired = required(
          intl.formatMessage({
            id: 'EditListingFeatureForm.priceRequired',
          })
        );
        const minPrice = new Money(config.deliveryMinimumPriceSubUnit, config.currency);
        const minPriceRequired = moneySubUnitAmountAtLeast(
          intl.formatMessage(
            {
              id: 'EditListingFeatureForm.priceTooLow',
            },
            {
              minPrice: formatMoney(intl, minPrice),
            }
          ),
          config.deliveryMinimumPriceSubUnit
        );

        const priceValidators = config.deliveryMinimumPriceSubUnit
          ? composeValidators(priceRequired, minPriceRequired)
          : priceRequired;
        const nonEmptyArrayMess = intl.formatMessage({ id: "EditListingFeatureForm.nonEmptyArrayMess" });

        return (
          <Form
            className={classes}
            onSubmit={e => {
              submittedValues.current = values;
              handleSubmit(e);
            }}
          >
            {errorMessage}
            {errorMessageShowListing}

            <OnChange name={'deliveryType'}>
              {value => {
                const newItems = value || [];
                if (newItems.length === 0) {
                  form.change('deliveryType', [PICKUP]);
                }
              }}
            </OnChange>

            <FieldCheckboxGroup
              className={css.features}
              id={'deliveryType'}
              name={'deliveryType'}
              label={deliveryTypeLabel}
              options={options}
              validate={required(deliveryTypeRequireMessage)}
            />

            {values.deliveryType && values.deliveryType.includes('delivery') ? (
              <>
                <FieldCurrencyInput
                  id="deliveryFee"
                  name="deliveryFee"
                  className={css.priceInput}
                  label={deliveryFeeLabel}
                  placeholder={deliveryFeeDescriptionMessage}
                  currencyConfig={config.currencyConfig}
                  validate={priceValidators}
                />

                <FieldArray name="locations" validate={nonEmptyArray(nonEmptyArrayMess)}>
                  {({ fields, meta }) => (
                    <>
                      {fields.map((name, index) => (
                        <div className={css.fieldItem} key={name}>
                          <div className={css.removeButton}>
                            <InlineTextButton type="button" onClick={() => fields.remove(index)}>
                              <FormattedMessage id="EditListingFeaturesForm.remove" />
                            </InlineTextButton>
                          </div>
                          <LocationAutocompleteInputField
                            className={css.locationAddress}
                            inputClassName={css.locationAutocompleteInput}
                            iconClassName={css.locationAutocompleteInputIcon}
                            predictionsClassName={css.predictionsRoot}
                            validClassName={css.validLocation}
                            // autoFocus
                            name={name}
                            label={addressLabelMessage(index + 1)}
                            placeholder={addressPlaceholderMessage}
                            useDefaultPredictions={false}
                            format={identity}
                            valueFromForm={values.location && values.location[index]}
                          />
                        </div>
                      ))}
                      <ValidationError fieldMeta={meta} />
                      <div className={css.buttonWrapper} onClick={() => fields.push(null)}>
                        <div className={css.plusIcon}></div>
                        <div className={css.buttonContent}>
                          <FormattedMessage id="EditListingFeaturesForm.addLocation" />
                        </div>
                      </div>
                    </>
                  )}
                </FieldArray>
              </>
            ) : null}

            <EditListingActionButton
              invalid={invalid}
              nextBtnMessage={saveActionMsg}
              nextBtnClassName={css.submitButton}
              form={form}
              onBackWithoutSubmit={onBackWithoutSubmit}
              submitInProgress={submitInProgress}
              submitReady={submitReady}
              submitDisabled={submitDisabled}
            />
          </Form>
        );
      }}
    />
  );
};

EditListingFeaturesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  fetchErrors: null,
  filterConfig: config.custom.filters,
};

EditListingFeaturesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  filterConfig: propTypes.filterConfig,
};

const EditListingFeaturesForm = EditListingFeaturesFormComponent;

export default EditListingFeaturesForm;
